// Lib
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

// Utils
import { waitUntil } from '../../../common/utils/functionUtils';
import { isPlatformIframe } from '../../platform/utils/platformDetailsUtils';
import getClientConfig from '../../utils/getClientConfig';
import { getOneTrustLanguageCode } from './oneTrustLocalizationUtils';
import { hasOneTrustConsentState } from './oneTrustUtils';

// Selectors
import { getPlatformDetailsSelector } from '../../platform/platformSelector';

// Constants
import { OneTrustStatus } from './oneTrustTypes';

const oneTrustLoaded = () => !!window.OneTrust && !!window.OneTrust.Init && !!window.OneTrust.GetDomainData;
const ONE_TRUST_SCRIPT_ID = 'onetrust-sdk-stub';
const GUEST_USER_ID = 'guest';

const useOneTrustLoader = ({
    isGuest,
    hasAuthenticatedCurrentUser,
    userId,
    userOTToken,
    userLocalePreference,
}: {
    isGuest: boolean;
    hasAuthenticatedCurrentUser: boolean;
    userId: string;
    userOTToken: string;
    userLocalePreference: string;
}): [OneTrustStatus] => {
    const config = getClientConfig();

    const platformDetails = useSelector(getPlatformDetailsSelector);
    const [oneTrustStatus, setOneTrustStatus] = useState<OneTrustStatus>(OneTrustStatus.UNFETCHED);

    const loadOneTrust = useCallback(
        (userId?: string, userOTToken?: string) => {
            const oneTrustLanguageCode = getOneTrustLanguageCode(userLocalePreference);

            // If user authenticated, set user information in window.OneTrust.dataSubjectParams before loading OneTrust
            // - This will be used to get user's cookie preference from OneTrust cloud, or send user's cookie preference
            //   to OneTrust Cloud
            // - This will help in Safari, where user's local cookie pref is removed by Safari every 7 days due to ITP

            window.OneTrust = window.OneTrust || {};
            window.OneTrust.dataSubjectParams = {};

            if (userId && userOTToken) {
                window.OneTrust.dataSubjectParams = {
                    id: userId,
                    token: userOTToken,
                    isAnonymous: false,
                };
            }

            // If OneTrust has not been loaded yet, embed OneTrust script into the DOM
            if (oneTrustStatus === OneTrustStatus.UNFETCHED) {
                const { domainScript } = config.oneTrust!;

                const existingScript = document.getElementById(ONE_TRUST_SCRIPT_ID);
                if (!existingScript) {
                    const script = document.createElement('script');
                    script.id = ONE_TRUST_SCRIPT_ID;
                    script.src = `https://cdn.cookielaw.org/consent/${domainScript}/otSDKStub.js`;
                    script.dataset.domainScript = domainScript;
                    script.dataset.language = oneTrustLanguageCode;

                    document.documentElement.lang = oneTrustLanguageCode;
                    document.body.appendChild(script);
                }

                setOneTrustStatus(OneTrustStatus.FETCHING);

                waitUntil(oneTrustLoaded, { duration: 60000 })
                    .then(() => {
                        setOneTrustStatus(OneTrustStatus.INITIALISED);
                    })
                    .catch(() => {
                        setOneTrustStatus(OneTrustStatus.ERROR);
                    });

                return;
            }

            // If OneTrust has previously been loaded, just sync the user cookie profile from OneTrust cloud
            if (oneTrustStatus === OneTrustStatus.INITIALISED) {
                window.OneTrust.syncConsentProfile?.(userId, userOTToken, false);
            }
        },
        [config.oneTrust, oneTrustStatus, userLocalePreference],
    );

    useEffect(() => {
        // If in iframe, don't show cookie banner, and use default cookie settings per region
        if (isPlatformIframe(platformDetails)) return;

        const isGuestUserId = userId === GUEST_USER_ID;
        const isWaitingForAuthenticatedUserDetails = !hasAuthenticatedCurrentUser || !userId || isGuestUserId;
        if (!isGuest && isWaitingForAuthenticatedUserDetails) return;

        if (isGuest) {
            loadOneTrust();
            return;
        }

        loadOneTrust(userId, userOTToken);
    }, [hasAuthenticatedCurrentUser, isGuest, loadOneTrust, platformDetails, userId, userOTToken]);

    useEffect(() => {
        if (oneTrustStatus !== OneTrustStatus.INITIALISED) return;

        const oneTrustLanguageCode = getOneTrustLanguageCode(userLocalePreference);

        // changeLanguage() can reset implicit targeting opt-in consent in Non-GDPR regions.
        if (hasOneTrustConsentState()) return;

        document.documentElement.lang = oneTrustLanguageCode;
        window.OneTrust?.changeLanguage?.(oneTrustLanguageCode);
    }, [oneTrustStatus, userLocalePreference]);

    return [oneTrustStatus];
};

export default useOneTrustLoader;
